import type {
  KeyboardEventHandler,
  MouseEventHandler,
  PointerEventHandler,
  TouchEventHandler
} from 'react';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSubheader
} from '@library/components/player';
import { CSS } from '@dnd-kit/utilities';
import { CloseIconButton } from '@library/components/buttons';
import { DateTime } from 'luxon';
import DraggableIcon from '@library/assets/images/Draggable.png';
import type { NormalizedProjectionWithPlayer } from '@library/types/streaks';
import { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';

export interface StreakBoardRankProjectionRowProps {
  projection: NormalizedProjectionWithPlayer;
  onUnrankProjectionWrapped: VoidFunction;
  temporaryRank: number;
}

export const StreakBoardRankProjectionRow = memo(
  ({
    projection,
    onUnrankProjectionWrapped,
    temporaryRank
  }: StreakBoardRankProjectionRowProps) => {
    const normalizedPlayer = projection.player;
    const imageUrl = normalizedPlayer?.attributes.image_url;
    const name = normalizedPlayer?.attributes?.name;
    const subheader = `${normalizedPlayer?.attributes.position} • ${normalizedPlayer?.attributes.team_name}`;
    const gameInfo = `${normalizedPlayer?.attributes.team} vs. ${projection.attributes.description} • ${DateTime.fromISO(projection.attributes.start_time).toFormat('t')}`;

    // Setup drag-and-drop for the row using useSortable
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id: projection.id });

    // Apply inline styles for transform and transition
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1 // Change opacity when dragging
    };

    // Explicitly cast event listeners to the correct types
    const onPointerDown = listeners?.onPointerDown as PointerEventHandler;
    const onMouseDown = listeners?.onMouseDown as MouseEventHandler;
    const onKeyDown = listeners?.onKeyDown as KeyboardEventHandler;
    const onTouchStart = listeners?.onTouchStart as TouchEventHandler;

    return (
      <tr
        key={projection?.id}
        className="relative h-5 text-white"
        style={style}
      >
        <td className="p-4">
          <div
            ref={setNodeRef}
            role="button"
            tabIndex={0}
            aria-pressed={attributes['aria-pressed']}
            onPointerDown={onPointerDown} // Pass event handlers explicitly
            onMouseDown={onMouseDown}
            onKeyDown={onKeyDown}
            onTouchStart={onTouchStart}
          >
            <PlayerProfile
              className="flex min-w-48 max-w-60 items-center gap-4"
              imageUrl={imageUrl}
              name={name ?? ''}
              subheader={subheader}
            >
              <img src={DraggableIcon} alt="Draggable" className="size-6" />
              <PlayerImage className="bg-soFresh-120 size-[49px] rounded-lg" />

              <div className="border-soFresh-110 flex h-14 flex-col items-start justify-start">
                <PlayerName className="font-heebo text-soClean-100 text-center text-sm font-medium leading-tight tracking-tight" />
                <PlayerSubheader className="text-soClean-170 font-heebo text-xs font-medium leading-5 tracking-tight" />
                <span className="text-soClean-170 font-heebo text-xs font-medium leading-5 tracking-tight">
                  {gameInfo}
                </span>
              </div>
            </PlayerProfile>
          </div>
        </td>
        <td className="p-4 text-center">{projection.attributes.line_score}</td>
        <td className="p-4 text-center">{projection.attributes.stat_type}</td>
        <td className="p-4 text-center">{temporaryRank}</td>
        <td>
          <div className="bg-soFresh-150 relative top-1/2 z-10 inline-flex size-8 items-center justify-center rounded-md">
            <CloseIconButton handleOnClick={onUnrankProjectionWrapped} />
          </div>
        </td>
      </tr>
    );
  }
);
